import Description from "components/long_form/Description";
import Dropzone from "react-dropzone";
import Label from "components/general/Label";
import PillButton from "components/general/PillButton";
import React from "react";
// import omit from "lodash/omit";
import styled from "styled-components";
import theme from "styles/theme";

const Container = styled.div`
  max-width: ${(props) => {
    let maxWidth = "460px";
    if (props.$isHalfWidth) {
      maxWidth = "222px";
    } else if (props.$isFullWidth) {
      maxWidth = "100%";
    }
    return maxWidth;
  }};
  margin-bottom: ${(props) => (props.$marginBottom ? props.$marginBottom : 0)};

  ${theme.media.phone`
    max-width: ${(props) => (props.$isFullWidth ? "100%" : "460px")};
    // margin: ${theme.spacing.unit * 0.5}px 0;
  `}
`;

const InputContainer = styled.div`
  position: relative;
  margin-top: ${theme.spacing.unit * 1}px;
`;

const StyledInput = styled.input`
  height: 56px;
  width: 100%;
  background: ${theme.colors.white};
  box-sizing: border-box;
  padding: 0 ${theme.spacing.unit * 2}px;

  // Border
  border: 2px solid
    ${(props) =>
      props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray2}`};
  border-radius: 16px;
  box-shadow: ${(props) =>
    props.$hasError ? "0px 2px 4px rgba(0, 0, 0, 0.1)" : "none"};
  outline: none;

  // Placeholders
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${(props) =>
      props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray3}`};
  }
  :-ms-input-placeholder {
    color: ${(props) =>
      props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray3}`};
  }

  // Text
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) =>
    props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray4}`};

  &:focus {
    // Border
    border: 2px solid
      ${(props) =>
        props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray4}`};
    box-shadow: "0px 2px 4px rgba(0, 0, 0, 0.1)";

    // Placeholders
    ::placeholder,
    ::-webkit-input-placeholder {
      color: ${(props) =>
        props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray4}`};
    }
    :-ms-input-placeholder {
      color: ${(props) =>
        props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray4}`};
    }

    // Text
    color: ${(props) =>
      props.$hasError ? `${theme.colors.red}` : `${theme.colors.gray4}`};
  }
`;

const InputHelper = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  display: flex;
  align-items: center;

  padding-right: ${theme.spacing.unit * 2}px;
  height: 56px;

  pointer-events: none;

  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.colors.blue};
`;

const Error = styled.div`
  margin-top: ${theme.spacing.unit * 1}px;

  // Text
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.red};
`;

const HelperText = styled.div`
  margin: ${theme.spacing.unit * 1}px 0 ${theme.spacing.unit * 0.5}px;

  font-family: Inter;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 150%;
  color: ${theme.colors.gray3};
`;

const UploadInputContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ChooseFileButton = styled(PillButton)`
  margin-left: ${theme.spacing.unit * 2}px;
`;

const Success = styled.div`
  position: absolute;
  right: 16px;
  top: 19.5px;

  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.colors.green};
`;

const Input = (props) => {
  const {
    className,
    isImageUpload,
    isFileUpload,
    onChooseFile,
    isFullWidth,
    isHalfWidth,
    marginBottom,
    label,
    description, // This is for Greenhouse-only html descriptions
    shouldHideLabel,
    mobileLabel,
    isOptional,
    isRequired,
    inputHelperText,
    error,
    touched,
    helperText,
    hasSuccess,
    ...rest
  } = props;

  const hasError = error && touched;

  // const plainProps = omit(props, "onChooseFile");
  let input = (
    <>
      <StyledInput {...rest} $hasError={hasError}></StyledInput>
      {hasSuccess && <Success>Success!</Success>}
    </>
  );
  if (isImageUpload) {
    input = (
      <UploadInputContainer>
        <StyledInput {...rest} $hasError={hasError} disabled />
        <Dropzone
          onDropAccepted={onChooseFile}
          onDropRejected={() => console.log("Image upload rejected")}
          accept={["image/png", "image/jpeg", "image/gif"]}
          maxFiles={1}
          noDrag={true}
        >
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps()} data-cy="drag-and-drop">
                <input {...getInputProps()} />
                <ChooseFileButton isGhost type="button">
                  Choose image
                </ChooseFileButton>
              </div>
            </section>
          )}
        </Dropzone>
      </UploadInputContainer>
    );
  }
  if (isFileUpload) {
    input = (
      <UploadInputContainer>
        <StyledInput {...rest} $hasError={hasError} disabled />
        <Dropzone
          onDropAccepted={onChooseFile}
          onDropRejected={() => console.log("File upload rejected")}
          accept={"application/pdf"}
          maxFiles={1}
          noDrag={true}
        >
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps()} data-cy="drag-and-drop">
                <input {...getInputProps()} />
                <ChooseFileButton isGhost type="button">
                  Choose file
                </ChooseFileButton>
              </div>
            </section>
          )}
        </Dropzone>
      </UploadInputContainer>
    );
  }

  return (
    <Container
      className={className}
      $isFullWidth={isFullWidth}
      $isHalfWidth={isHalfWidth}
      $marginBottom={marginBottom}
    >
      {shouldHideLabel ? null : (
        <Label
          text={label}
          mobileText={mobileLabel}
          isOptional={isOptional}
          isRequired={isRequired}
        />
      )}
      <Description html={description} />
      <InputContainer>
        {input}
        {inputHelperText && <InputHelper>{inputHelperText}</InputHelper>}
      </InputContainer>
      {hasError && <Error>{error}</Error>}
      {helperText && <HelperText>{helperText}</HelperText>}
    </Container>
  );
};

export default Input;
